// ────────────────────────────────────────────────────────────────
// app.routes.ts — top-level routing (§6.1)
// /sign-in is public; everything else lives under the authenticated
// Shell layout, lazy-loaded and gated by authGuard + capabilityGuard.
// All Phase A feature screens are implemented (WP-1…WP-7);
// Phase B adds the browsers (WP-B1), Customer 360 and catalog analytics.
// ────────────────────────────────────────────────────────────────

import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { capabilityGuard } from './core/guards/capability.guard';
import { Shell } from './core/layout/shell';

export const routes: Routes = [
  {
    path: 'sign-in',
    loadComponent: () => import('./features/auth/sign-in/sign-in').then((m) => m.SignIn),
  },
  {
    // Public reset-password page reached from the emailed single-use link (§3.6).
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: '',
    component: Shell,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'exceptions' },
      {
        path: 'exceptions',
        canActivate: [capabilityGuard],
        data: { capabilities: ['exceptions.read'] },
        loadComponent: () =>
          import('./features/exceptions/exceptions-dashboard').then((m) => m.ExceptionsDashboard),
      },
      {
        path: 'exceptions/:type',
        canActivate: [capabilityGuard],
        data: { capabilities: ['exceptions.read'] },
        loadComponent: () =>
          import('./features/exceptions/exception-drilldown').then((m) => m.ExceptionDrilldown),
      },
      {
        path: 'health',
        canActivate: [capabilityGuard],
        data: { capabilities: ['health.read'] },
        loadComponent: () => import('./features/health/health').then((m) => m.Health),
      },
      {
        path: 'maintenance',
        canActivate: [capabilityGuard],
        data: { capabilities: ['maintenance.read'] },
        loadComponent: () =>
          import('./features/maintenance/toggles/maintenance-toggles').then((m) => m.MaintenanceToggles),
      },
      {
        path: 'maintenance/planned',
        canActivate: [capabilityGuard],
        data: { capabilities: ['maintenance.read'] },
        loadComponent: () =>
          import('./features/maintenance/planned/planned-events').then((m) => m.PlannedEvents),
      },
      {
        path: 'promotions',
        canActivate: [capabilityGuard],
        data: { capabilities: ['coupons.read'] },
        loadComponent: () =>
          import('./features/promotions/coupons/coupon-list').then((m) => m.CouponList),
      },
      {
        path: 'promotions/coupons/new',
        canActivate: [capabilityGuard],
        data: { capabilities: ['coupons.write'] },
        loadComponent: () =>
          import('./features/promotions/coupons/coupon-wizard').then((m) => m.CouponWizard),
      },
      {
        path: 'promotions/coupons/:id',
        canActivate: [capabilityGuard],
        data: { capabilities: ['coupons.read'] },
        loadComponent: () =>
          import('./features/promotions/coupons/coupon-wizard').then((m) => m.CouponWizard),
      },
      {
        path: 'promotions/campaigns',
        canActivate: [capabilityGuard],
        data: { capabilities: ['coupons.read'] },
        loadComponent: () =>
          import('./features/promotions/campaigns/campaigns').then((m) => m.Campaigns),
      },
      {
        path: 'c360',
        canActivate: [capabilityGuard],
        data: { capabilities: ['c360.read'] },
        loadComponent: () =>
          import('./features/customer-360/search/c360-search').then((m) => m.C360Search),
      },
      {
        path: 'c360/user/:id',
        canActivate: [capabilityGuard],
        data: { capabilities: ['c360.read'] },
        loadComponent: () =>
          import('./features/customer-360/user/user-360').then((m) => m.User360),
      },
      {
        path: 'c360/business/:id',
        canActivate: [capabilityGuard],
        data: { capabilities: ['c360.read'] },
        loadComponent: () =>
          import('./features/customer-360/business/business-360').then((m) => m.Business360),
      },
      {
        path: 'c360/community/:id',
        canActivate: [capabilityGuard],
        data: { capabilities: ['c360.read'] },
        loadComponent: () =>
          import('./features/customer-360/community/community-360').then((m) => m.Community360),
      },
      {
        path: 'catalog',
        canActivate: [capabilityGuard],
        data: { capabilities: ['catalog.read'] },
        loadComponent: () =>
          import('./features/catalog-analytics/catalog-teams').then((m) => m.CatalogTeams),
      },
      {
        path: 'catalog/team/:id',
        canActivate: [capabilityGuard],
        data: { capabilities: ['catalog.read'] },
        loadComponent: () =>
          import('./features/catalog-analytics/catalog-team-drilldown').then((m) => m.CatalogTeamDrilldown),
      },
      {
        path: 'browsers/feedbacks',
        canActivate: [capabilityGuard],
        data: { capabilities: ['feedbacks.read'] },
        loadComponent: () =>
          import('./features/browsers/feedbacks/feedback-browser').then((m) => m.FeedbackBrowser),
      },
      {
        path: 'browsers/logs',
        canActivate: [capabilityGuard],
        data: { capabilities: ['logs.read'] },
        loadComponent: () =>
          import('./features/browsers/logs/log-browser').then((m) => m.LogBrowser),
      },
      {
        path: 'commissions',
        canActivate: [capabilityGuard],
        data: { capabilities: ['commissions.read'] },
        loadComponent: () => import('./features/commissions/commissions').then((m) => m.Commissions),
      },
      {
        path: 'reference-data',
        pathMatch: 'full',
        redirectTo: 'reference-data/taarif',
      },
      {
        path: 'reference-data/taarif',
        canActivate: [capabilityGuard],
        data: { capabilities: ['reference-data.read'] },
        loadComponent: () =>
          import('./features/reference-data/taarif/taarif-editor').then((m) => m.TaarifEditor),
      },
      {
        path: 'reference-data/countries-tax',
        canActivate: [capabilityGuard],
        data: { capabilities: ['reference-data.read'] },
        loadComponent: () =>
          import('./features/reference-data/countries-tax/countries-tax-editor').then((m) => m.CountriesTaxEditor),
      },
      {
        path: 'analytics',
        canActivate: [capabilityGuard],
        data: { capabilities: ['analytics.read'] },
        loadChildren: () => import('./features/analytics/analytics.routes').then((m) => m.ANALYTICS_ROUTES),
      },
      {
        path: 'audit',
        canActivate: [capabilityGuard],
        data: { capabilities: ['audit.read'] },
        loadComponent: () => import('./features/audit/audit-browser').then((m) => m.AuditBrowser),
      },
      {
        path: 'settings',
        canActivate: [capabilityGuard],
        data: { capabilities: ['config.read'] },
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      {
        path: 'help',
        loadComponent: () => import('./help/help').then((m) => m.Help),
      },
      {
        path: 'help/:slug',
        loadComponent: () => import('./help/help').then((m) => m.Help),
      },
      {
        path: 'account/totp',
        loadComponent: () => import('./features/auth/totp-enroll/totp-enroll').then((m) => m.TotpEnroll),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
